import React from "react";
import { useParams, Link } from "react-router-dom";
import { servicesData } from "../data/mockData";
import { ArrowRight, CheckCircle2, ArrowLeft } from "lucide-react";

const ServiceDetail = () => {
  const { serviceId } = useParams();
  const service = servicesData.find((s) => s.id === serviceId);

  if (!service) {
    return (
      <div className="pt-40 pb-24 text-center">
        <div className="max-w-[1320px] mx-auto px-6">
          <h1
            className="text-[36px] font-bold mb-4"
            style={{ fontFamily: "'Poppins', sans-serif", color: "#1B1B3A" }}
          >
            Service Not Found
          </h1>
          <Link
            to="/services"
            className="inline-flex items-center gap-2 text-[#10B981] text-[15px] font-semibold no-underline hover:gap-3 transition-all"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            <ArrowLeft size={16} /> Back to Services
          </Link>
        </div>
      </div>
    );
  }

  const otherServices = servicesData.filter((s) => s.id !== serviceId);

  return (
    <div className="overflow-hidden">

      {/* ── Header ── */}
      <section className="pt-32 pb-8 md:pt-40 md:pb-12">
        <div className="max-w-[1320px] mx-auto px-6">
          <Link
            to="/services"
            className="inline-flex items-center gap-2 text-[#555] text-[14px] no-underline hover:text-[#10B981] transition-colors mb-6"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            <ArrowLeft size={16} /> Back to Services
          </Link>
          <p
            className="text-[#10B981] text-[15px] font-semibold mb-2"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            Urban Hunter Services
          </p>

          {/* Logo if present (Amazon Ads etc.) */}
          {service.logo && (
            <div className="mb-6">
              <img
                src={service.logo}
                alt={service.title}
                className="h-[48px] md:h-[60px] object-contain"
              />
            </div>
          )}

          <h1
            className="text-[40px] md:text-[56px] font-bold leading-tight mb-4"
            style={{ fontFamily: "'Poppins', sans-serif", color: "#1B1B3A" }}
          >
            {service.title}
          </h1>
          <p
            className="text-[#555] text-[17px] leading-relaxed max-w-[700px]"
            style={{ fontFamily: "'Poppins', sans-serif" }}
          >
            {service.shortDescription}
          </p>
        </div>
      </section>

      {/* ── Content ── */}
      <section className="pb-16 md:pb-24">
        <div className="max-w-[1320px] mx-auto px-6">
          <div className="flex flex-col lg:flex-row gap-12">

            {/* ── Main Content ── */}
            <div className="lg:w-2/3">
              <p
                className="text-[#555] text-[16px] leading-relaxed mb-10 whitespace-pre-line"
                style={{ fontFamily: "'Poppins', sans-serif" }}
              >
                {service.fullDescription}
              </p>

              {/* Rich sections (Amazon Ads style) */}
              {service.sections ? (
                <div className="space-y-12 mb-10">
                  {service.sections.map((section, si) => (
                    <div key={si}>
                      <div className="flex items-center gap-3 mb-3">
                        <span className="text-[28px]">{section.icon}</span>
                        <h2
                          className="text-[22px] font-bold"
                          style={{ fontFamily: "'Poppins', sans-serif", color: "#1B1B3A" }}
                        >
                          {section.title}
                        </h2>
                      </div>
                      <p
                        className="text-[#555] text-[15px] leading-relaxed mb-6"
                        style={{ fontFamily: "'Poppins', sans-serif" }}
                      >
                        {section.description}
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {section.points.map((point, pi) => (
                          <div
                            key={pi}
                            className="bg-[#F8F6FF] rounded-2xl p-6 border border-[#ede9ff]"
                          >
                            <div className="flex items-start gap-3 mb-2">
                              <CheckCircle2
                                size={18}
                                className="text-[#10B981] mt-0.5 flex-shrink-0"
                              />
                              <h4
                                className="text-[15px] font-bold"
                                style={{ fontFamily: "'Poppins', sans-serif", color: "#1B1B3A" }}
                              >
                                {point.title}
                              </h4>
                            </div>
                            <p
                              className="text-[#555] text-[13px] leading-relaxed pl-7"
                              style={{ fontFamily: "'Poppins', sans-serif" }}
                            >
                              {point.desc}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                /* Standard feature list for other services */
                <>
                  <h3
                    className="text-[20px] font-bold mb-6"
                    style={{ fontFamily: "'Poppins', sans-serif", color: "#1B1B3A" }}
                  >
                    What we offer:
                  </h3>
                  <ul className="space-y-4 mb-10">
                    {service.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <CheckCircle2
                          size={20}
                          className="text-[#F26522] mt-0.5 flex-shrink-0"
                        />
                        <span
                          className="text-[#444] text-[15px] leading-relaxed"
                          style={{ fontFamily: "'Poppins', sans-serif" }}
                        >
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {/* Why Urban Hunter box — only for Amazon Ads */}
              {service.id === "amazon-ads" && (
                <div className="bg-[#1B1B3A] rounded-2xl p-8 mb-10">
                  <h3
                    className="text-[20px] font-bold mb-4 text-white"
                    style={{ fontFamily: "'Poppins', sans-serif" }}
                  >
                    🔥 Why High Growth Brands Choose Urban Hunter
                  </h3>
                  <ul className="space-y-3">
                    {[
                      "Authorized Amazon Ads Partner",
                      "Proven experience across global markets",
                      "Expertise in managing high spend accounts",
                      "Deep understanding of competitive marketplace dynamics",
                      "Founder led strategy & execution",
                    ].map((item, i) => (
                      <li key={i} className="flex items-center gap-3">
                        <CheckCircle2 size={16} className="text-[#10B981] flex-shrink-0" />
                        <span
                          className="text-white/80 text-[14px]"
                          style={{ fontFamily: "'Poppins', sans-serif" }}
                        >
                          {item}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* CTA */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/free-audit"
                  className="inline-flex items-center gap-2 bg-[#10B981] text-white px-8 py-4 rounded-full text-[14px] font-semibold tracking-wider uppercase no-underline hover:bg-[#059669] transition-colors duration-300"
                  style={{ fontFamily: "'Poppins', sans-serif" }}
                >
                  Request a Private Growth Audit
                  <ArrowRight size={16} />
                </Link>
                <Link
                  to="/contact-us"
                  className="inline-flex items-center gap-2 bg-[#1B1B3A] text-white px-8 py-4 rounded-full text-[14px] font-semibold tracking-wider uppercase no-underline hover:bg-[#2a2a5a] transition-colors duration-300"
                  style={{ fontFamily: "'Poppins', sans-serif" }}
                >
                  Speak With a Strategy Expert
                </Link>
              </div>
            </div>

            {/* ── Sidebar ── */}
            <div className="lg:w-1/3">
              <div className="bg-[#F8F6FF] rounded-2xl p-8 sticky top-32">
                <h3
                  className="text-[16px] font-bold mb-6 uppercase tracking-wider"
                  style={{ fontFamily: "'Poppins', sans-serif", color: "#1B1B3A" }}
                >
                  Other Services
                </h3>
                <div className="space-y-3">
                  {otherServices.map((s) => (
                    <Link
                      key={s.id}
                      to={s.link}
                      className="flex items-center justify-between p-4 bg-white rounded-xl no-underline group hover:shadow-md transition-all duration-300"
                    >
                      <span
                        className="text-[#1B1B3A] text-[14px] font-medium group-hover:text-[#10B981] transition-colors"
                        style={{ fontFamily: "'Poppins', sans-serif" }}
                      >
                        {s.title}
                      </span>
                      <ArrowRight
                        size={14}
                        className="text-[#ccc] group-hover:text-[#10B981] transition-colors"
                      />
                    </Link>
                  ))}
                </div>

                {/* Mini CTA in sidebar */}
                <div className="mt-8 bg-[#1B1B3A] rounded-xl p-6">
                  <p
                    className="text-white text-[14px] font-semibold mb-3"
                    style={{ fontFamily: "'Poppins', sans-serif" }}
                  >
                    Ready to scale with precision?
                  </p>
                  <Link
                    to="/free-audit"
                    className="inline-flex items-center gap-2 bg-[#10B981] text-white px-5 py-3 rounded-full text-[12px] font-semibold tracking-wider uppercase no-underline hover:bg-[#059669] transition-colors duration-300 w-full justify-center"
                    style={{ fontFamily: "'Poppins', sans-serif" }}
                  >
                    Get a Free Audit
                    <ArrowRight size={13} />
                  </Link>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;
